import React, { useState } from "react";
import { isEmpty } from "lodash";
import { Col, Form, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";

import {
	HookFormInputGroup,
	HookSelectAsyncFormGroup,
	HookSelectFormGroup,
} from "components";
import { LineDivider } from "components/layout";
import { DebounceTimeout } from "globalConstants";
import { getFullName } from "helpers";
import { paymentsService } from "services";
import { WithholdStatuses } from "types";
import {
	trimMidSentenceNormalizer,
	trimNormalizer,
	float2Normalizer,
} from "validators";
import { required } from "validators/validation";

import StatusRow from "./rowComponents/merchantStatusRow";
import OptionLabel from "./rowComponents/optionLabel";

import styles from "../paymentsModal.module.scss";

type WithholdTotalFormProps = {
	paidMonth: string;
	paidYear: string;
	isEditing?: boolean;
	initialValues?: { [key: string]: any };
};

export const WithholdTotalForm = ({
	paidMonth,
	paidYear,
	isEditing = false,
	initialValues,
}: WithholdTotalFormProps) => {
	const {
		register,

		formState: { errors },
	} = useFormContext();

	const [withholdAmount, setWithholdAmount] = useState<string | null>(null);

	const withholdReasons = WithholdStatuses.getOptions();

	const loadAgents = async (pattern, prevOptions, { page }) => {
		try {
			const newAgents = await paymentsService.getPayoutsAgents({
				paidMonth,
				paidYear,
				page,
				pattern,
			});

			const hasMore =
				newAgents.items.length !== 0 &&
				prevOptions.length + newAgents.items.length < newAgents.count;

			return {
				options: newAgents.items,
				hasMore,
				additional: {
					page: page + 1,
				},
			};
		} catch {
			return { options: [] };
		}
	};

	const handleAgentChanged = value =>
		setWithholdAmount(value?.total_payout_amount);

	const getAgentOption = value => (
		<OptionLabel
			value={getFullName(value.agent)}
			isDisabled={value.is_total_withhold}
		/>
	);

	return (
		<>
			{isEditing ? (
				<Form.Group as={Row}>
					<Col>
						<div className={styles.opacityColor}>
							Agent to Apply Total Withhold
						</div>
						<div>{getFullName(initialValues?.agent)}</div>
					</Col>
				</Form.Group>
			) : (
				<HookSelectAsyncFormGroup
					name="agent"
					label="Agent to Apply Total Withhold"
					getOptionLabel={getAgentOption}
					getOptionValue={option => option.agent_id}
					loadOptions={loadAgents}
					additional={{ page: 0 }}
					placeholder="Please Select"
					debounceTimeout={DebounceTimeout}
					blurLoading={!isEmpty(loadAgents)}
					cacheOptions
					isSearchable
					defaultOptions
					defaultValue={null}
					rules={{
						validate: {
							required,
						},
					}}
					labelSize={{ sm: 12 }}
					controlSize={{ sm: 12 }}
					onChange={handleAgentChanged}
					isOptionDisabled={option => option.is_total_withhold}
					showRequiredMark
				/>
			)}

			{withholdAmount !== null && (
				<Form.Group as={Row}>
					<Col>
						<div className={styles.withholdAmount}>
							Withhold Amount:
							<span className="text-light ml-1">
								{float2Normalizer(withholdAmount, "$")}
							</span>
						</div>
						<LineDivider className="pt-2" />
					</Col>
				</Form.Group>
			)}

			<HookSelectFormGroup
				label="Withhold Reason"
				options={withholdReasons}
				getOptionLabel={option => option.displayName}
				getOptionValue={option => option.value}
				placeholder="Please Select"
				name="withhold_reason"
				isSearchable={false}
				defaultValue={null}
				cacheOptions
				defaultOptions
				debounceTimeout={DebounceTimeout}
				colSelect={{ sm: 12 }}
				rules={[]}
			/>

			<HookFormInputGroup
				name="description"
				register={register}
				label="Comments"
				colInput={12}
				colLabel={12}
				autoComplete="off"
				normalize={trimMidSentenceNormalizer}
				normalizeOnBlur={trimNormalizer}
				errors={errors}
				as="textarea"
				rows={5}
			/>

			<Form.Group as={Row}>
				<Col>
					<StatusRow register={register} title="agent" />
				</Col>
			</Form.Group>
			<LineDivider className="pt-2" />
		</>
	);
};
